import { useSelector } from "react-redux";

import { useGetUserAccessQuery } from "state/rtkQuery/access/accessQueries";

// The signed-in user's effective role for a specific organization, sourced
// from GET /assigned/user/roles (real, always-fresh RBAC data) rather than the
// old state.auth.permissions populated once at login. Distinct from a global
// role: someone can be OrganizationOwner of org A and hold no role at all in
// org B — this hook answers "what am I in *this* org."
//
// "Leadership" and "OperationsUser" can both appear here as per-org role
// values (alongside OrganizationOwner/OrganizationApprover), separate from
// any global role name of the same shape returned in the same response's
// global roles list. Both are deliberately org-scoped, NOT a blanket
// organization:read override — only EnterpriseAdmin/OperationsAdmin still
// get cross-org access via the global permission override (see
// RequireEcrOpsRole's isCrossOrgAdminRole).
export const useEffectiveOrgRole = (orgId) => {
  const email = useSelector((state) => state.auth?.userAccount?.username);

  const { data, isLoading, isError } = useGetUserAccessQuery({ email }, { skip: !email });

  const entry = data?.organization?.find((org) => org.org_id === orgId) ?? null;
  const role = entry?.role ?? null;

  return {
    role,
    isOwner: role === "OrganizationOwner",
    isApprover: role === "OrganizationApprover",
    isLeadership: role === "Leadership",
    // OperationsUser is org-scoped only (never a blanket global/cross-org
    // grant — see RequireEcrOpsRole's isCrossOrgAdminRole, which deliberately
    // excludes it) but gets full OrganizationOwner-equivalent write access on
    // orgs it's actually assigned to, same shape as isOwner/isApprover above.
    isOperationsUser: role === "OperationsUser",
    // isLoading only (not isFetching) — see useAccessPermissions.js's note:
    // gating a route guard's <Outlet /> on isFetching unmounts and remounts
    // the routed page on every background refetch (e.g. any mutation
    // elsewhere invalidating the same query), wiping its local state.
    isLoading,
    isError,
  };
};
